import React, { useEffect, useState } from 'react';
import {User, MapPin, CreditCard, ChevronDown, ChevronUp, AlertCircle, ArrowLeft} from 'lucide-react';
import { useAppDispatch, useAppSelector } from '../../store/hooks';
import { fetchMedicalRequests } from '../../store/slices/Medical_Requests/MedicalrequestSlice';
import {useNavigate} from "react-router-dom";

interface Field {
  key: string;
  value: string | number | null;
}

interface MedicalRequestItem {
  [index: string]: Field;
}

interface RefundItem {
  [index: string]: Field;
}

interface ServiceCategory {
  key: string;
  value: MedicalRequestItem[] | RefundItem;
}

interface MainSection {
  key: string;
  value: ServiceCategory[];
}

// Component-specific types
interface DynamicField {
  key: string;
  value: any;
  label?: string;
}

interface BaseMedicalItem {
  id: string;
  category: string;
  fields: DynamicField[];
  status?: string;
  authorizedServices?: Array<{ key: string; value: string }>;
}

interface CategoryConfigType {
  icon: any;
  type: string;
  expandableFields: string[];
}

type TabType = 'services' | 'refunds';

const getCategoryIcon = (categoryKey: string) => {
  const lowerKey = categoryKey.toLowerCase();

  if (lowerKey.includes('həkim') ||
    lowerKey.includes('qeydiyyat') ||
    lowerKey.includes('onlayn') ||
    lowerKey.includes('doctor') ||
    lowerKey.includes('appointment') ||
    lowerKey.includes('registration')) {
    return User;
  }
  else if (lowerKey.includes('xəstəxana') ||
    lowerKey.includes('müraciət') ||
    lowerKey.includes('hospital') ||
    lowerKey.includes('request')) {
    return MapPin;
  }
  else if (lowerKey.includes('geri') ||
    lowerKey.includes('ödəniş') ||
    lowerKey.includes('refund') ||
    lowerKey.includes('возврат')) {
    return CreditCard;
  }

  return User;
};

const hasExpandableFields = (categoryKey: string): string[] => {
  const lowerKey = categoryKey.toLowerCase();
  if (lowerKey.includes('xəstəxana') || lowerKey.includes('hospital')) {
    return ['3'];
  }
  return [];
};

const MedicalAppointmentsApp: React.FC = () => {
  const dispatch = useAppDispatch();
  const { medicalRequests, loading, error } = useAppSelector((state) => state.medicalRequests);
  const navigate = useNavigate();

  const [currentLanguage] = useState<'az' | 'en' | 'ru'>(() => {
    const lang = localStorage.getItem('selectedLanguage') || 'az';
    return (lang === 'az' || lang === 'en' || lang === 'ru') ? lang : 'az';
  });

  const translations = {
    az: {
      title: "Tibbi müraciətlər",
      subtitle: "Bu bölmədə Tibbi müraciətlərinizi tapa bilər və ətraflı məlumat ala bilərsiniz",
      all: "Hamısı",
      allowedServices: "İcazə verilmiş xidmətlər",
      noData: "Məlumat tapılmadı",
      noDataDescription: "Hal-hazırda göstəriləcək tibbi müraciət yoxdur",
      loading: "Məlumatlar yüklənir...",
      errorOccurred: "Xəta baş verdi",
      retryButton: "Yenidən cəhd edin",
      back: "Geri",

    },
    ru: {
      title: "Медицинские обращения",
      subtitle: "В этом разделе вы можете найти свои медицинские обращения и получить подробную информацию",
      all: "Все",
      allowedServices: "Разрешённые услуги",
      noData: "Данные не найдены",
      noDataDescription: "В настоящее время нет медицинских обращений для отображения",
      loading: "Загрузка данных...",
      errorOccurred: "Произошла ошибка",
      retryButton: "Попробовать снова",
      back: "Назад",
    },
    en: {
      title: "Medical Requests",
      subtitle: "In this section you can find your medical requests and get detailed information",
      all: "All",
      allowedServices: "Authorized Services",
      noData: "No data found",
      noDataDescription: "Currently there are no medical requests to display",
      loading: "Loading data...",
      errorOccurred: "An error occurred",
      retryButton: "Try again",
      back: "Back",
    }
  };

  const t = translations[currentLanguage];

  const [activeTab, setActiveTab] = useState<TabType>('services');
  const [activeFilter, setActiveFilter] = useState<string>('all');
  const [expandedCards, setExpandedCards] = useState<Set<string>>(new Set());
  const [servicesLabel, setServicesLabel] = useState<string>('Xidmətlər');
  const [refundsLabel, setRefundsLabel] = useState<string>('Geri ödənişlər');

  useEffect(() => {
    const apiLanguage = currentLanguage === 'ru' ? 'en' : currentLanguage;
    dispatch(fetchMedicalRequests(apiLanguage as 'az' | 'en'));
  }, [dispatch, currentLanguage]);

  useEffect(() => {
    if (medicalRequests && medicalRequests.length > 0) {
      const servicesSection = medicalRequests.find((section: MainSection) =>
        section.value && Array.isArray(section.value) && section.value.length > 0
      );
      if (servicesSection) {
        setServicesLabel(servicesSection.key);
      }

      const refundsSection = medicalRequests.find((section: MainSection, index: number) =>
        index > 0 && section.key
      );
      if (refundsSection) {
        setRefundsLabel(refundsSection.key);
      }
    }
  }, [medicalRequests]);

  const toggleCard = (id: string) => {
    setExpandedCards(prev => {
      const newSet = new Set(prev);
      if (newSet.has(id)) {
        newSet.delete(id);
      } else {
        newSet.add(id);
      }
      return newSet;
    });
  };

  const getStatusInfo = (status: string): { color: string; text: string } => {
    const colorMatch = status.match(/0x[A-Fa-f0-9]{8}/);
    let colorClass = 'bg-gray-100 text-gray-700';
    let displayText = status;

    if (colorMatch) {
      const hexColor = colorMatch[0];
      displayText = status.replace(hexColor, '').trim();
      const cssColor = '#' + hexColor.slice(4);

      switch(cssColor.toUpperCase()) {
        case '#4169FF':
          colorClass = 'bg-[#4169FF] text-white';
          break;
        case '#8A00F0':
          colorClass = 'bg-[#8A00F0] text-white';
          break;
        case '#FF8D05':
          colorClass = 'bg-[#FF8D05] text-white';
          break;
        case '#0C9F51':
          colorClass = 'bg-[#0C9F51] text-white';
          break;
        default:
          colorClass = `bg-[${cssColor}] text-white`;
      }
    }
    displayText = displayText
      .replace(/\|/g, '')
      .replace(/[-_#]\s*\d+/g, '')
      .replace(/\s*[-_#]\s*/g, ' ')
      .replace(/\d+/g, '')
      .trim();

    return { color: colorClass, text: displayText };
  };

  const formatFieldValue = (value: any, fieldLabel: string): any => {
    if (!value) return '';

    const lowerLabel = fieldLabel?.toLowerCase() || '';
    if (lowerLabel.includes('məbləğ') ||
      lowerLabel.includes('amount') ||
      lowerLabel.includes('сумма')) {
      if (String(value).includes('AZN')) {
        return String(value);
      }
      const numValue = Number(value);
      if (!isNaN(numValue)) {
        return `${numValue} AZN`;
      }
    }

    return String(value);
  };

  const transformData = (): { services: BaseMedicalItem[], refunds: BaseMedicalItem[] } => {
    const services: BaseMedicalItem[] = [];
    const refunds: BaseMedicalItem[] = [];

    if (!medicalRequests || medicalRequests.length === 0) {
      return { services, refunds };
    }

    medicalRequests.forEach((mainSection: MainSection, sectionIndex: number) => {
      const sectionValue = mainSection.value;

      if (!Array.isArray(sectionValue)) {
        return;
      }

      const isServicesSection = sectionIndex === 0;

      sectionValue.forEach((category: ServiceCategory, categoryIndex: number) => {
        const categoryKey = category.key;
        const categoryData = category.value;

        if (Array.isArray(categoryData)) {
          categoryData.forEach((item: MedicalRequestItem, itemIndex: number) => {
            const fields: DynamicField[] = [];
            let statusValue = '';
            const authorizedServicesList: Array<{ key: string; value: string }> = [];

            Object.keys(item).forEach(fieldKey => {
              const field = item[fieldKey];
              const fieldValue = field?.value;
              const fieldLabel = field?.key || '';

              if (fieldValue !== undefined && fieldValue !== null && fieldValue !== '') {
                if (typeof fieldValue === 'string' && fieldValue.includes('0x')) {
                  statusValue = String(fieldValue);
                }
                else if (fieldLabel?.toLowerCase().includes('icazə') ||
                  fieldLabel?.toLowerCase().includes('xidmət') ||
                  fieldLabel?.toLowerCase().includes('service') ||
                  fieldLabel?.toLowerCase().includes('authorized') ||
                  fieldLabel?.toLowerCase().includes('разрешённ') ||
                  fieldLabel?.toLowerCase().includes('allowed')) {
                  authorizedServicesList.push({
                    key: fieldLabel,
                    value: String(fieldValue)
                  });
                }
                else {
                  fields.push({
                    key: fieldKey,
                    value: formatFieldValue(fieldValue, fieldLabel),
                    label: fieldLabel
                  });
                }
              }
            });

            if (fields.length > 0 || authorizedServicesList.length > 0) {
              const itemId = `${isServicesSection ? 'service' : 'item'}-${categoryKey}-${itemIndex}`;
              const item: BaseMedicalItem = {
                id: itemId,
                category: categoryKey,
                fields,
                status: statusValue,
                authorizedServices: authorizedServicesList.length > 0 ? authorizedServicesList : undefined
              };

              if (isServicesSection) {
                services.push(item);
              } else {
                refunds.push(item);
              }
            }
          });
        } else if (categoryData && typeof categoryData === 'object') {
          const fields: DynamicField[] = [];
          let statusValue = '';

          Object.keys(categoryData).forEach(fieldKey => {
            const field = categoryData[fieldKey];
            const fieldValue = field?.value;
            const fieldLabel = field?.key || '';

            if (fieldValue !== undefined && fieldValue !== null && fieldValue !== '') {
              if (typeof fieldValue === 'string' && fieldValue.includes('0x')) {
                statusValue = String(fieldValue);
              } else {
                fields.push({
                  key: fieldKey,
                  value: formatFieldValue(fieldValue, fieldLabel),
                  label: fieldLabel
                });
              }
            }
          });

          if (fields.length > 0) {
            const item: BaseMedicalItem = {
              id: `refund-${categoryIndex}`,
              category: categoryKey,
              fields,
              status: statusValue
            };

            refunds.push(item);
          }
        }
      });
    });

    return { services, refunds };
  };

  const getCurrentData = (): BaseMedicalItem[] => {
    const data = transformData();
    return activeTab === 'services' ? data.services : data.refunds;
  };

  const getFilteredData = (): BaseMedicalItem[] => {
    const data = getCurrentData();
    if (activeFilter === 'all') return data;
    return data.filter(item => item.category === activeFilter);
  };

  const getCategories = (): string[] => {
    const data = getCurrentData();
    const categories = new Set(data.map(item => item.category));
    return Array.from(categories);
  };

  const getCategoryConfig = (categoryName: string): CategoryConfigType => {
    return {
      icon: getCategoryIcon(categoryName),
      type: categoryName.toLowerCase().replace(/\s+/g, '-'),
      expandableFields: hasExpandableFields(categoryName)
    };
  };

  if (loading) {
    return (
      <div className=" flex  justify-center items-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-colorpink mx-auto mb-4"></div>
          <p className="text-gray-600">{t.loading}</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <div className="text-center bg-red-50 rounded-2xl p-8 max-w-md">
          <AlertCircle className="w-12 h-12 text-red-600 mx-auto mb-4" />
          <h3 className="text-lg font-semibold text-red-800 mb-2">{t.errorOccurred}</h3>
          <p className="text-red-600 mb-4">{error}</p>
          <button
            onClick={() => {
              const apiLanguage = currentLanguage === 'ru' ? 'en' : currentLanguage;
              dispatch(fetchMedicalRequests(apiLanguage as 'az' | 'en'));
            }}
            className="px-6 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors"
          >
            {t.retryButton}
          </button>
        </div>
      </div>
    );
  }

  const filteredData = getFilteredData();
  const categories = getCategories();

  const shouldShowCategoryNav = !(activeTab === 'refunds' && activeFilter === 'all' && categories.length <= 1);

  return (
    <div>
      <div className="gap-4 pb-1">
        <h1 className="text-3xl font-bold text-stone-800">
          {t.title}
        </h1>
      </div>

      <div className="mx-auto">
        <p className="text-stone-600 leading-relaxed">
          {t.subtitle}
        </p>

        {/* Tab Buttons */}
        <div className="flex justify-center gap-3 m-5">
          <div>
            <button
              onClick={() => {
                setActiveTab('services');
                setActiveFilter('all');
              }}
              className={`flex-1 py-4 px-6 rounded-2xl transition-all duration-300 shadow-md ${
                activeTab === 'services'
                  ? 'bg-gradient-to-r from-pink-700 to-pink-600 text-white shadow-rose-300/50 scale-105'
                  : 'bg-white/80 text-stone-700 hover:bg-white hover:shadow-lg'
              }`}
            >
              {servicesLabel}
            </button>
          </div>
          <div>
            <button
              onClick={() => {
                setActiveTab('refunds');
                setActiveFilter('all');
              }}
              className={`flex-1 py-4 px-6 rounded-2xl transition-all duration-300 shadow-md ${
                activeTab === 'refunds'
                  ? 'bg-gradient-to-r from-pink-700 to-pink-600 text-white shadow-rose-300/50 scale-105'
                  : 'bg-white/80 text-stone-700 hover:bg-white hover:shadow-lg'
              }`}
            >
              {refundsLabel}
            </button>
          </div>
        </div>

        {/* Dynamic Filter Tabs */}
        {shouldShowCategoryNav && categories.length > 0 && (
          <div className="flex gap-2 mb-6 overflow-x-auto pb-2">
            <button
              onClick={() => setActiveFilter('all')}
              className={`px-5 py-2.5 rounded-full whitespace-nowrap transition-all ${
                activeFilter === 'all'
                  ? 'bg-rose-100 text-pink-800 shadow-md'
                  : 'bg-white/70 text-stone-600 hover:bg-white'
              }`}
            >
              {t.all}
            </button>
            {categories.map(category => (
              <button
                key={category}
                onClick={() => setActiveFilter(category)}
                className={`px-5 py-2.5 rounded-full whitespace-nowrap transition-all ${
                  activeFilter === category
                    ? 'bg-rose-100 text-pink-800 shadow-md'
                    : 'bg-white/70 text-stone-600 hover:bg-white'
                }`}
              >
                {category}
              </button>
            ))}
          </div>
        )}

        {/* Empty State */}
        {filteredData.length === 0 && (
          <div className="text-center py-16">
            <div className="w-20 h-20 bg-stone-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <AlertCircle className="w-10 h-10 text-stone-400" />
            </div>
            <h3 className="text-lg font-semibold text-stone-700 mb-2">
              {t.noData}
            </h3>
            <p className="text-stone-500">
              {t.noDataDescription}
            </p>
          </div>
        )}

        {/* Cards Grid */}
        <div className="grid grid-cols-1 min-[500px]:grid-cols-2 min-[1420px]:grid-cols-3 gap-6">
          {filteredData.map((item: BaseMedicalItem, index: number) => {
            const config = getCategoryConfig(item.category);
            const IconComponent = config.icon;
            const isExpanded = expandedCards.has(item.id);

            return (
              <div
                key={item.id}
                className="bg-white/90 backdrop-blur-sm rounded-3xl p-6 border border-stone-200/50 shadow-lg hover:shadow-2xl transition-all duration-300 hover:-translate-y-1"
                style={{ animationDelay: `${index * 50}ms` }}
              >
                {/* Card Header */}
                <div className="flex items-center gap-3 mb-5">
                  <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-pink-600 to-pink-700 flex items-center justify-center shadow-md">
                    <IconComponent className="w-5 h-5 text-white" />
                  </div>
                  <h3 className="flex-1 text-stone-800 leading-tight font-semibold">
                    {item.category}
                  </h3>
                </div>

                {/* Dynamic Fields */}
                <div className="space-y-3 mb-4">
                  {item.fields.map((field, fieldIndex) => (
                    <div key={fieldIndex} className="flex justify-between items-center">
                      <span className="text-sm text-stone-500">{field.label}</span>
                      <span className={`text-sm font-medium text-right ${
                        field.label?.toLowerCase().includes('məbləğ') ||
                        field.label?.toLowerCase().includes('amount')
                          ? 'text-pink-600 font-semibold'
                          : 'text-stone-800'
                      }`}>
                        {field.value}
                      </span>
                    </div>
                  ))}

                  {/* Authorized Services Section */}
                  {item.authorizedServices && item.authorizedServices.length > 0 && (
                    <div className="pt-2 pb-2 border-t border-stone-100">
                      <button
                        onClick={() => toggleCard(item.id)}
                        className="w-full flex items-center justify-between text-sm text-stone-500 mb-2 hover:text-stone-700 transition-colors"
                      >
                        <span>{t.allowedServices}</span>
                        {isExpanded ? (
                          <ChevronUp className="w-4 h-4" />
                        ) : (
                          <ChevronDown className="w-4 h-4" />
                        )}
                      </button>
                      {isExpanded && (
                        <ul className="flex flex-col gap-2 animate-fadeIn pl-5">
                          {item.authorizedServices.map((service, idx) => (
                            <li
                              key={idx}
                              className="text-xs list-disc"
                            >
                              {service.value}
                            </li>
                          ))}
                        </ul>
                      )}
                    </div>
                  )}
                </div>

                {/* Status Badge */}
                {item.status && (
                  <div className="pt-4 border-t border-stone-100">
                    <span className={`inline-block text-xs px-3 py-1.5 rounded-full font-medium ${getStatusInfo(item.status).color}`}>
                      {getStatusInfo(item.status).text}
                    </span>
                  </div>
                )}
              </div>
            );
          })}
        </div>
        {/* Back Button */}
        <div className="mt-8 px-2 md:px-0">
          <button
            onClick={() => navigate(-1)}
            className="flex items-center gap-2 px-4 py-2 text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-[#a80067] hover:text-white hover:border-[#a80067] transition-all duration-200 shadow-sm hover:shadow font-medium"
          >
            <ArrowLeft className="w-4 h-4 sm:w-5 sm:h-5" />
            <span className="text-sm sm:text-base">{t.back}</span>
          </button>
        </div>
      </div>

      <style>{`
        * {
          -webkit-font-smoothing: antialiased;
          -moz-osx-font-smoothing: grayscale;
        }
        
        @keyframes fadeInUp {
          from {
            opacity: 0;
            transform: translateY(20px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }
        
        @keyframes fadeIn {
          from {
            opacity: 0;
            max-height: 0;
          }
          to {
            opacity: 1;
            max-height: 200px;
          }
        }
        
        .grid > div {
          animation: fadeInUp 0.6s ease-out backwards;
        }
        
        .animate-fadeIn {
          animation: fadeIn 0.3s ease-out;
        }
      `}</style>
    </div>
  );
};

export default MedicalAppointmentsApp;